import React, { useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../firebase';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import BlueprintWithMarkers from '../../../Components/BlueprintWithMarkers';
import BlueprintUploadModal from '../../../Components/BlueprintUploadModal';
import BlueprintRelocationWizard from '../../../Components/BlueprintRelocationWizard';
import ConfirmationModal from '../../../Components/ConfirmationModal';
import { useRoofDrawing } from '../../../hooks/roofPlan';
import { ROOF_PLAN_LABEL, roofPlanBlueprintsPath, roofPlanCurrentBlueprintKeyPath } from '../../../Helpers/roofPlan';
import { partitionForView } from '../../../Helpers/componentDrawing';
import ListView from './ListView';
import '../../Level/LevelPage.css';
import './RoofDrawingSection.css';

interface RoofDrawingSectionProps {
    building: Building;
    buildingKey: string;
    /** The takplan: every D6 component (roofs, what is mounted on them, takfönster). */
    components: BaseComponentImpl[];
    selectedKey: string | null;
    onComponentClick: (componentKey: string) => void;
    isAdmin: boolean;
}

const REMOVE_DRAWING_TEXT =
    'Takritningen slutar visas på taksidan. Bilden och markörernas positioner på den behålls, som för en utbytt ritning.';

/**
 * The "Takritning" view of the roof page: the roof drawing at `structure/roofPlan`
 * with the takplan components as markers and the rest listed as "Ej placerade".
 * Admins upload, replace (relocation wizard when markers sit on the old drawing)
 * and remove the drawing here, as for level drawings. Web creates no components,
 * so placing markers is relocation-only.
 */
const RoofDrawingSection: React.FC<RoofDrawingSectionProps> = ({
    building,
    buildingKey,
    components,
    selectedKey,
    onComponentClick,
    isAdmin,
}) => {
    const [user] = useAuthState(auth);
    const {
        roofDrawing,
        relocation,
        error,
        clearError,
        handleDeferredUpload,
        commitRelocation,
        abortRelocation,
        removeDrawing,
    } = useRoofDrawing(buildingKey);

    const [isDragOver, setIsDragOver] = useState(false);
    const [showBlueprintUpload, setShowBlueprintUpload] = useState(false);
    const [droppedFile, setDroppedFile] = useState<File | null>(null);
    const [dropError, setDropError] = useState<string | null>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [removing, setRemoving] = useState(false);

    const { placedComponents, unplacedComponents } = useMemo(() => {
        if (!roofDrawing) return { placedComponents: [], unplacedComponents: [] };
        // The view is built from the hook's drawing, which can run ahead of the building context right after an upload.
        const view = { kind: 'roofPlan' as const, imageKey: roofDrawing.key, image: roofDrawing.image, label: ROOF_PLAN_LABEL };
        const { placed, unplaced } = partitionForView(components, view, building.model.structure);
        return { placedComponents: placed, unplacedComponents: unplaced };
    }, [components, roofDrawing, building]);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (isAdmin) setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (!isAdmin) return;

        const file = e.dataTransfer.files[0];
        if (!file) return;
        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
            setDroppedFile(file);
            setShowBlueprintUpload(true);
        } else {
            setDropError('Endast bilder (PNG, JPG, JPEG) och PDF-filer är tillåtna.');
        }
    };

    const handleBlueprintModalClose = () => {
        setShowBlueprintUpload(false);
        setDroppedFile(null);
    };

    const handleRemoveConfirmed = async () => {
        setRemoving(true);
        try {
            await removeDrawing();
        } finally {
            setRemoving(false);
            setConfirmRemove(false);
        }
    };

    return (
        <div className="roof-drawing-section">
            {error && (
                <Alert variant="danger" dismissible onClose={clearError}>{error}</Alert>
            )}
            {dropError && (
                <Alert variant="danger" dismissible onClose={() => setDropError(null)}>{dropError}</Alert>
            )}

            {isAdmin && (
                <div className="roof-drawing-toolbar">
                    <button type="button" onClick={() => setShowBlueprintUpload(true)} className="blueprint-change-button">
                        {roofDrawing ? 'Byt takritning' : 'Ladda upp takritning'}
                    </button>
                    {roofDrawing && (
                        <button type="button" onClick={() => setConfirmRemove(true)} className="roof-drawing-remove-button">
                            Ta bort ritning
                        </button>
                    )}
                </div>
            )}

            {roofDrawing ? (
                <BlueprintWithMarkers
                    key={`${roofDrawing.key}-${placedComponents.length}`}
                    blueprint={roofDrawing.image}
                    currentBlueprintKey={roofDrawing.key}
                    building={building}
                    utilities={placedComponents}
                    selectedUtilityKey={selectedKey}
                    onUtilityClick={onComponentClick}
                    hideTitle
                    compactMarkers
                />
            ) : (
                <div
                    className={`blueprint-placeholder ${isDragOver ? 'blueprint-placeholder--drag-over' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <span className="blueprint-placeholder-text">
                        {isDragOver ? 'Släpp för att ladda upp' : 'Takritning saknas'}
                    </span>
                    {isAdmin && (
                        <>
                            <button type="button" onClick={() => setShowBlueprintUpload(true)} className="blueprint-upload-button">
                                Ladda upp
                            </button>
                            <span className="blueprint-placeholder-hint">
                                eller dra och släpp en fil här
                            </span>
                        </>
                    )}
                </div>
            )}

            {roofDrawing && unplacedComponents.length > 0 && (
                <div>
                    <h4 className="generic-component-section-header">
                        Ej placerade på takritningen:
                    </h4>
                    <ListView
                        components={unplacedComponents}
                        building={building}
                        buildingKey={buildingKey}
                        selectedKey={selectedKey}
                        onComponentClick={onComponentClick}
                        groupingStrategy="none"
                    />
                </div>
            )}

            <BlueprintUploadModal
                isOpen={showBlueprintUpload}
                onClose={handleBlueprintModalClose}
                deferPointer
                onDeferredUpload={handleDeferredUpload}
                buildingKey={buildingKey}
                target={{
                    blueprintsPath: roofPlanBlueprintsPath(buildingKey),
                    currentBlueprintKeyPath: roofPlanCurrentBlueprintKeyPath(buildingKey),
                }}
                authorUid={user?.uid || ''}
                initialFile={droppedFile}
            />

            {relocation && (
                <BlueprintRelocationWizard
                    building={building}
                    source={relocation.source}
                    target={relocation.target}
                    items={relocation.items}
                    onCommit={commitRelocation}
                    onAbort={abortRelocation}
                />
            )}

            <ConfirmationModal
                show={confirmRemove}
                title="Ta bort takritning"
                message={REMOVE_DRAWING_TEXT}
                onConfirm={handleRemoveConfirmed}
                onCancel={() => setConfirmRemove(false)}
                loading={removing}
            />
        </div>
    );
};

export default RoofDrawingSection;
